import { some, sum } from 'lodash';

import { useCustomerInfo } from '@/api/customers';
import { useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { useSubscriptionsInfo } from '@/api/subscriptions';
import { Null } from '@/components/Null';
import { composeLineCountMessage } from '@/components/transactions/TransactionLineChangeSummary';
import { TransactionLink } from '@/components/transactions/TransactionLink';
import {
    InlineAlert,
    InlineAlertBody,
    InlineAlertPrimaryButton,
    InlineAlertSecondaryButton,
    InlineAlertTitle,
} from '@/components/ui/InlineAlert';
import { enabledLineCounts, subscribedLineCounts } from '@/context/transaction/TelemetryLineChangeContext';
import { UNUSED_LINES_NOTIFICATION_ID } from '@/hooks/useNotifications';

export const UnusedLinesAlert = () => {
    const { data: customerInfo } = useCustomerInfo();
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    const { data: subscriptionTagsInfo } = useSubscriptionTagsInfo();
    const isOnTelemetryPage = location.pathname.match(/\/telemetry\b/);

    if (!customerInfo || !subscriptionInfo?.data) {
        return false;
    }

    const isPastDue = subscriptionInfo.data.length && subscriptionInfo.data[0].status === 'past_due';

    const subscribedLines = subscribedLineCounts(subscriptionInfo.data[0]?.items ?? []);
    const usedLines = enabledLineCounts(subscriptionTagsInfo?.data);
    const unusedCounts = {
        cellular: subscribedLines.cellular - usedLines.cellular,
        iridium: subscribedLines.iridium - usedLines.iridium,
    };

    if (isPastDue) {
        // if they're past due, they need to resolve that before they can do anything else,
        // so suppress this alert since it's pointless and to reduce alert fatigue
        return <Null data-testid="unused-lines-past-due" />;
    }

    if (some(unusedCounts, (n) => n < 0)) {
        return <UndersubscribedAlert />;
    }

    const totalUnused = sum(Object.values(unusedCounts));
    if (!totalUnused) {
        return <Null data-testid="unused-lines-none" />;
    }

    const title = `You have ${composeLineCountMessage(unusedCounts, '', 'unused')}.`;
    const plural = totalUnused !== 1;
    const body = plural
        ? 'These unused lines are available to assign to devices. You will continue to be billed for them unless they are removed.'
        : 'This unused line is available to assign to a device. You will continue to be billed for it unless it is removed.';

    return (
        <InlineAlert
            dismissNotificationId={UNUSED_LINES_NOTIFICATION_ID}
            severity="info"
            actionButton={
                <>
                    {!isOnTelemetryPage && (
                        <InlineAlertSecondaryButton href="/telemetry" testid="unused-lines">
                            View telemetry
                        </InlineAlertSecondaryButton>
                    )}
                    <InlineAlertPrimaryButton
                        component={TransactionLink}
                        to="/payment/unusedLines"
                        testid="unused-lines"
                    >
                        Remove unused line{plural && 's'}
                    </InlineAlertPrimaryButton>
                </>
            }
        >
            <InlineAlertTitle testid="unused-lines">{title}</InlineAlertTitle>
            <InlineAlertBody testid="unused-lines">{body}</InlineAlertBody>
        </InlineAlert>
    );
};

const UndersubscribedAlert = () => {
    const isOnTelemetryPage = location.pathname.match(/\/telemetry\b/);

    return (
        <InlineAlert
            severity="error"
            actionButton={
                <>
                    {!isOnTelemetryPage && (
                        <InlineAlertSecondaryButton href="/telemetry" testid="undersubscribed-lines">
                            View telemetry
                        </InlineAlertSecondaryButton>
                    )}
                    <InlineAlertPrimaryButton
                        component={TransactionLink}
                        to="/payment/unusedLines"
                        testid="undersubscribed-lines"
                    >
                        Update subscription
                    </InlineAlertPrimaryButton>
                </>
            }
        >
            <InlineAlertTitle testid="undersubscribed-lines">
                You have more lines in use than are included in your subscription
            </InlineAlertTitle>
            <InlineAlertBody testid="undersubscribed-lines">
                Your device telemetry may be disabled unless you update your subscription.
            </InlineAlertBody>
        </InlineAlert>
    );
};
